import { Routes } from '@angular/router';
import { authHomeGuard } from './core/guards/auth-home.guard';
import { tenantGuard } from './core/guards/tenant.guard';
import { callbackGuard } from './core/guards/callback.guard';
import { permissionGuard, permissionCanMatch } from './core/guards/permission.guard';
import { FEATURE } from './core/constants/feature-keys';
import { legalRedirectGuard } from './core/constants/legal-links';
import { slidesEditorMobileGuard } from './core/guards/slides-editor-mobile.guard';

// Re-check permissions on every navigation — without runGuardsAndResolvers:'always' the guard is
// skipped when only the parent :tenantId param changes (tenant switch), leaving the user on a page
// of a tenant where they lack the permission. permissionGuard is wired as both canActivate
// (authoritative, uses the route param) and canMatch (prevents lazy-loading a forbidden module).
export const routes: Routes = [
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full',
  },
  {
    path: 'callback',
    loadComponent: () =>
      import('./features/auth/callback/callback.page').then(
        (m) => m.AuthCallbackPage,
      ),
    canActivate: [callbackGuard],
  },
  {
    path: 'login/callback',
    loadComponent: () =>
      import('./features/auth/callback/callback.page').then(
        (m) => m.AuthCallbackPage,
      ),
    canActivate: [callbackGuard],
  },
  {
    path: 'home',
    loadComponent: () => import('./home/home.page').then((m) => m.HomePage),
    canActivate: [authHomeGuard],
  },
  // Legal texts are published once for the whole group in the Gastro Management portal
  // (see core/constants/legal-links.ts). These routes stay only so existing deep links
  // and bookmarks keep working; each hands off to the portal and renders nothing itself.
  {
    path: 'impressum',
    canActivate: [legalRedirectGuard('impressum')],
    children: [],
  },
  {
    path: 'datenschutz',
    canActivate: [legalRedirectGuard('datenschutz')],
    children: [],
  },
  {
    path: 'agb',
    canActivate: [legalRedirectGuard('agb')],
    children: [],
  },
  {
    path: ':tenantId',
    canActivate: [tenantGuard],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        // Home/dashboard stays ungated — it is every tenant member's landing page.
        path: 'home',
        loadComponent: () =>
          import('./home/home.page').then((m) => m.HomePage),
      },
      {
        path: 'screens',
        loadComponent: () =>
          import('./features/screens/screens.page').then(
            (m) => m.ScreensPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.screens, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'playlists',
        loadComponent: () =>
          import('./features/playlists/playlists.page').then(
            (m) => m.PlaylistsPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.playlists, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'slides',
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.slides, level: 'read' },
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: ':slideId/editor',
            loadComponent: () =>
              import('./features/slides/slide-editor-v2/slide-editor-v2.modal').then(
                (m) => m.SlideEditorV2ModalComponent,
              ),
            canActivate: [slidesEditorMobileGuard],
          },
          {
            path: ':slideId/preview',
            loadComponent: () =>
              import('./features/slides/slide-editor-v2/slide-editor-v2.modal').then(
                (m) => m.SlideEditorV2ModalComponent,
              ),
            data: { preview: true },
            canActivate: [slidesEditorMobileGuard],
          },
          {
            path: '',
            loadComponent: () =>
              import('./features/slides/slides.page').then((m) => m.SlidesPage),
          },
        ],
      },
      {
        path: 'analytics',
        loadComponent: () =>
          import('./features/analytics/analytics.page').then(
            (m) => m.AnalyticsPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.analytics, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'banners',
        loadComponent: () =>
          import('./features/banners/banners.page').then(
            (m) => m.BannersPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.banners, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'seasons',
        loadComponent: () =>
          import('./features/seasons/seasons.page').then(
            (m) => m.SeasonsPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.seasons, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'alerts',
        loadComponent: () =>
          import('./features/alerts/alerts.page').then((m) => m.AlertsPage),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.alerts, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'admin/users',
        loadComponent: () =>
          import('./features/user-management/user-management.page').then(
            (m) => m.UserManagementPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { requireAdmin: true },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'admin/groups',
        loadComponent: () =>
          import('./features/group-management/group-management.page').then(
            (m) => m.GroupManagementPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { requireAdmin: true },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'admin/settings',
        loadComponent: () =>
          import('./features/admin-settings/admin-settings.page').then(
            (m) => m.AdminSettingsPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.tenantSettings, level: 'write' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'admin/integrations',
        loadComponent: () =>
          import(
            './features/admin-integrations/admin-integrations.page'
          ).then((m) => m.AdminIntegrationsPage),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.integrations, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'admin/audit-log',
        loadComponent: () =>
          import('./features/admin-audit-log/admin-audit-log.page').then(
            (m) => m.AdminAuditLogPage,
          ),
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.auditLog, level: 'read' },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'support',
        canActivate: [permissionGuard],
        canMatch: [permissionCanMatch],
        data: { feature: FEATURE.support, level: 'read' },
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/support/support-list/support-list.page').then(
                (m) => m.SupportListPage,
              ),
          },
          {
            path: ':conversationId',
            loadComponent: () =>
              import('./features/support/support-chat/support-chat.page').then(
                (m) => m.SupportChatPage,
              ),
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/home',
  },
];
